import React, { FC, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router'
import BellOffIcon from 'transfix-icons/build/BellOffIcon'
import BellOnIcon from 'transfix-icons/build/BellOnIcon'
import Card from 'transfix-ui/components/Card'
import { formatRelativeTime } from 'transfix-ui/components/RelativeDateTime'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { SavedLanesDeleteModal } from 'apps/fleet-planner/components/saved-lanes/SavedLanesDeleteModal'
import { useUserContext } from 'apps/fleet-planner/context'
import { useSavedLanesContext } from 'apps/fleet-planner/context/SavedLanesContext'
import { useTrackConversionContext } from 'apps/fleet-planner/context/track-conversion-source'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { BookableShipmentSavedSearch } from 'apps/fleet-planner/types'
import { useModalState } from 'utils/hooks'
import LaneDetails from '../LaneDetails'
import LanePickupDelivery from '../LanePickupDelivery'
import LoadsAvailableTag from '../LoadsAvailableTag'
import SavedLaneRowMenu from '../SaveLaneRowMenu'
import SavedLaneFormModal from '../SavedLaneFormModal'
import {
  getSavedLaneBookingFilters,
  getSavedLaneBookingLink,
  getSavedLaneSourceSegment,
} from '../utils'
import { useSavedLaneRowStyles } from './useSavedLaneRowStyles'

const DELIMITER = '  ·  '

export interface ISavedLaneRowProps {
  savedLane: BookableShipmentSavedSearch
}

export const SavedLaneRow: FC<ISavedLaneRowProps> = ({ savedLane }) => {
  const { setTrackConversion } = useTrackConversionContext()
  const { trackEvent } = useFMSAnalytics()
  const { isMobile } = useBreakpointContext()
  const classes = useSavedLaneRowStyles()
  const history = useHistory()

  const {
    hasNotificationsEnabled,
    id: savedLaneId,
    motorCarrier,
    sourceType,
    totalResultCount = 0,
    updatedAt = '',
  } = savedLane || {}
  const { legalName: motorCarrierName } = motorCarrier || {}

  /**
   * Saved Lanes Context
   */
  const {
    refetch: refetchSavedLanes,
    removeSavedLaneFromState,
    updateSavedLanesState,
  } = useSavedLanesContext()

  /**
   * Current User
   */
  const { userData } = useUserContext()
  const { motorCarriers = [] } = userData || {}
  const showMotorCarrier = motorCarriers.length > 1

  const rowClickHandler = () => {
    const sourceSegment = getSavedLaneSourceSegment(sourceType)
    if (sourceSegment) {
      setTrackConversion('web', sourceSegment)
    }

    trackEvent('Saved Lanes', 'CLICKED_SAVED_LANE_ROW')
    const filters = getSavedLaneBookingFilters(savedLane)
    history.push(getSavedLaneBookingLink(filters))
  }

  const { createToggleHandler, modalState } = useModalState({
    deleteConfirmationModal: false,
    editModal: false,
  })
  const { deleteConfirmationModal: isDeleteModalOpen, editModal: isEditModalOpen } =
    modalState || {}

  const openDeleteModalHandler = createToggleHandler('deleteConfirmationModal', true)
  const closeDeleteModalHandler = createToggleHandler('deleteConfirmationModal', false)

  const openEditModalHandler = createToggleHandler('editModal', true)
  const closeEditModalHandler = createToggleHandler('editModal', false)

  const deleteSuccessHandler = useCallback(() => {
    closeDeleteModalHandler()
    if (savedLaneId) {
      removeSavedLaneFromState(savedLaneId)
    }
  }, [closeDeleteModalHandler, removeSavedLaneFromState, savedLaneId])

  const editSuccessHandler = useCallback(() => {
    closeEditModalHandler()
    refetchSavedLanes()
  }, [closeEditModalHandler, refetchSavedLanes])

  const notificationToggleSuccessHandler = useCallback(
    (updatedSavedLane: BookableShipmentSavedSearch) => {
      updateSavedLanesState(updatedSavedLane)
    },
    [updateSavedLanesState]
  )

  const laneTimestamp = useMemo(() => {
    const timestamp = `Saved ${formatRelativeTime({
      datetime: updatedAt,
    })} ago by ${sourceType === 'dispatcher_created' ? `you` : `Transfix`}`

    if (showMotorCarrier) {
      return [timestamp, motorCarrierName].join(DELIMITER)
    }
    return timestamp
  }, [updatedAt, motorCarrierName, showMotorCarrier, sourceType])

  return (
    <Card dataTest='saved-lane-row' className={classes.card} noPadding>
      <Box onClick={rowClickHandler}>
        <div className={classes.mainBox}>
          <div className={classes.boxStyle}>
            {!isMobile &&
              (hasNotificationsEnabled ? (
                <BellOnIcon size='large' data-test='saved-lane-bell-on' />
              ) : (
                <BellOffIcon size='large' data-test='saved-lane-bell-off' />
              ))}
            <div>
              <div className={classes.laneContainer}>
                <LanePickupDelivery {...savedLane} />
              </div>
              <div className={classes.detailsContainer}>
                <LaneDetails {...savedLane} />
              </div>
              <div>
                <Typography variant='caption' color='textSecondary' component='div'>
                  {laneTimestamp}
                </Typography>
              </div>
            </div>
          </div>
          <div className={classes.menuBox}>
            <LoadsAvailableTag loadsAvailableCount={totalResultCount} />
            {savedLaneId && (
              <SavedLaneRowMenu
                savedLaneId={savedLaneId}
                notifications={hasNotificationsEnabled}
                onDelete={openDeleteModalHandler}
                onEdit={openEditModalHandler}
                onNotificationToggleSuccess={notificationToggleSuccessHandler}
              />
            )}
          </div>
        </div>
      </Box>
      {isDeleteModalOpen && savedLaneId && (
        <SavedLanesDeleteModal
          savedLaneId={savedLaneId}
          onClose={closeDeleteModalHandler}
          onSuccess={deleteSuccessHandler}
        />
      )}
      {isEditModalOpen && (
        <SavedLaneFormModal
          savedLane={savedLane}
          onClose={closeEditModalHandler}
          onSuccess={editSuccessHandler}
        />
      )}
    </Card>
  )
}
export default SavedLaneRow
